/**
 * FeatureToggle
 *
 * A styled toggle switch component for binary pricing options.
 * Provides better UX than checkboxes with clear on/off state and optional pricing info.
 *
 * Visual pattern:
 * ┌─────────────────────────────────────────────┐
 * │ Isolated Database                      [ON] │
 * │ Dedicated database for enhanced security    │
 * │ $500/month                                  │
 * └─────────────────────────────────────────────┘
 *
 * @see src/modules/RetoolModule/components/PricingCalculatorForm.tsx - Main form
 */

import React from 'react';
import {
  Box,
  Switch,
  Typography,
  alpha,
  useTheme,
  Tooltip,
  Link,
  SxProps,
  Theme,
} from '@mui/material';
import { Info as InfoIcon, ExternalLink as ExternalLinkIcon } from 'lucide-react';

export interface FeatureToggleProps {
  /** Label for the toggle */
  label: string;
  /** Whether the toggle is on */
  checked: boolean;
  /** Callback when toggle changes */
  onChange: (checked: boolean) => void;
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Optional description text */
  description?: string;
  /** Optional price display (e.g., "$500/month") */
  price?: string;
  /** Optional tooltip for more info */
  tooltip?: string;
  /** Optional documentation link */
  docLink?: string;
  /** Optional note text shown below (e.g., "Included in Single Tenant") */
  note?: string;
  /** Note color variant */
  noteColor?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  /** Whether this is a dependent feature (auto-enabled by another) */
  isDependent?: boolean;
  /** Name attribute for the input */
  name?: string;
  /** Whether the toggle is locked (can't be turned off) with explanation */
  locked?: boolean;
  /** Explanation for why the toggle is locked */
  lockedReason?: string;
}

/** Card background/border, which shift on checked, dark mode, and disabled. */
const buildContainerSx = (
  theme: Theme,
  isDark: boolean,
  checked: boolean,
  disabled: boolean,
): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  p: 2,
  borderRadius: '10px',
  backgroundColor: checked
    ? isDark
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.04)
    : isDark
      ? alpha(theme.palette.background.paper, 0.4)
      : alpha(theme.palette.grey[100], 0.5),
  border: '1px solid',
  borderColor: checked
    ? alpha(theme.palette.primary.main, isDark ? 0.3 : 0.2)
    : alpha(theme.palette.divider, isDark ? 0.1 : 0.2),
  transition: 'all 0.2s ease',
  opacity: disabled ? 0.6 : 1,
  mb: 1.5,
  '&:hover': {
    borderColor: disabled ? undefined : alpha(theme.palette.primary.main, 0.4),
  },
});

/** Switch colouring; a locked-on toggle reads as a warning rather than primary. */
const buildSwitchSx = (
  theme: Theme,
  isDark: boolean,
  locked: boolean,
  checked: boolean,
): SxProps<Theme> => ({
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      color: locked && checked ? theme.palette.warning.main : theme.palette.primary.main,
      '& + .MuiSwitch-track': {
        backgroundColor:
          locked && checked ? theme.palette.warning.main : theme.palette.primary.main,
        opacity: 0.5,
      },
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: isDark
      ? alpha(theme.palette.common.white, 0.3)
      : alpha(theme.palette.common.black, 0.2),
  },
});

type FeatureToggleDetailsProps = Pick<
  FeatureToggleProps,
  'checked' | 'description' | 'price' | 'note' | 'isDependent' | 'locked' | 'lockedReason'
> & {
  theme: Theme;
  noteColor: NonNullable<FeatureToggleProps['noteColor']>;
};

/** Description, price, note, and the auto-enabled / locked explanations. */
const FeatureToggleDetails: React.FC<FeatureToggleDetailsProps> = ({
  theme,
  checked,
  description,
  price,
  note,
  noteColor,
  isDependent,
  locked,
  lockedReason,
}) => (
  <>
    {description && (
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: '0.8rem',
          lineHeight: 1.4,
        }}
      >
        {description}
      </Typography>
    )}

    {price && checked && (
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.primary.main,
          fontWeight: 500,
          fontSize: '0.85rem',
          mt: 0.5,
        }}
      >
        {price}
      </Typography>
    )}

    {note && (
      <Typography
        variant="body2"
        sx={{
          color: theme.palette[noteColor].main,
          fontSize: '0.75rem',
          mt: 0.5,
          fontStyle: 'italic',
        }}
      >
        {note}
      </Typography>
    )}

    {isDependent && checked && (
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.info.main,
          fontSize: '0.75rem',
          mt: 0.5,
        }}
      >
        Auto-enabled by another option
      </Typography>
    )}

    {locked && checked && lockedReason && (
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.warning.main,
          fontSize: '0.75rem',
          mt: 0.5,
          fontStyle: 'italic',
        }}
      >
        {lockedReason}
      </Typography>
    )}
  </>
);

export const FeatureToggle: React.FC<FeatureToggleProps> = ({
  label,
  checked,
  onChange,
  disabled = false,
  description,
  price,
  tooltip,
  docLink,
  note,
  noteColor = 'primary',
  isDependent = false,
  name,
  locked = false,
  lockedReason,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // If locked and trying to turn off, don't allow
    if (locked && checked && !event.target.checked) {
      return;
    }
    onChange(event.target.checked);
  };

  // When locked and checked, the switch should appear disabled but with a special style
  const isEffectivelyDisabled = disabled || (locked && checked);

  return (
    <Box sx={buildContainerSx(theme, isDark, checked, disabled)}>
      {/* Left side: Label, Description, Price */}
      <Box sx={{ flex: 1, pr: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: checked ? theme.palette.text.primary : theme.palette.text.secondary,
            }}
          >
            {label}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <InfoIcon size={14} color={theme.palette.text.secondary} style={{ cursor: 'help' }} />
            </Tooltip>
          )}
          {docLink && (
            <Link
              href={docLink}
              target="_blank"
              rel="noopener"
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.text.secondary,
                '&:hover': { color: theme.palette.primary.main },
              }}
            >
              <ExternalLinkIcon size={14} />
            </Link>
          )}
        </Box>

        <FeatureToggleDetails
          theme={theme}
          checked={checked}
          description={description}
          price={price}
          note={note}
          noteColor={noteColor}
          isDependent={isDependent}
          locked={locked}
          lockedReason={lockedReason}
        />
      </Box>

      {/* Right side: Switch */}
      <Tooltip
        title={locked && checked && lockedReason ? lockedReason : ''}
        arrow
        placement="top"
        disableHoverListener={!(locked && checked)}
      >
        <Box>
          <Switch
            checked={checked}
            onChange={handleChange}
            disabled={isEffectivelyDisabled}
            name={name}
            color="primary"
            sx={buildSwitchSx(theme, isDark, locked, checked)}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

export default FeatureToggle;
